/* gwp.js — class-name agnostic via [data-gwp="..."] hooks
 * Requires Liquid to add:
 *  - data-gwp="indicator" + data-threshold + data-label
 *  - data-gwp="marker" inside each indicator
 *  - data-gwp="segment" + data-start + data-end
 *  - data-gwp="fill" inside each segment
 *  - data-gwp="nextText", data-gwp="remainingAmount"
 *  - data-gwp="successMessage", data-gwp="labelsRow"
 * Wrapper still needs:
 *  - data-component="gwp-progressbar"
 *  - data-thresholds="0,5000,10000,..."
 *  - data-currency, data-cart-total, data-max-threshold
 *  - (for GWP manager) data-gwp-variant-id, data-gwp-tier, data-gwp-target-threshold
 */

// @ts-nocheck
(() => {
  const DEBUG = true;
  const log = (...a) => {
    if (DEBUG) console.log("[GWP]", ...a);
  };
  const err = (...a) => console.error("[GWP]", ...a);

  // ---------- Progress (role-based) ----------
  class GwpProgress {
    constructor(root) {
      this.root = root;
      this.currency = root.dataset.currency || "$";
      this.thresholds = this._parseThresholds(root.dataset.thresholds || "");
      this.cartPriceType = root.dataset.cartPriceType || "total_price";
      this.maxThreshold = parseInt(root.dataset.maxThreshold || "0", 10) || 0;
      this.lastSeen = {
        value: parseInt(root.dataset.cartTotal || "0", 10) || 0,
        t: Date.now(),
      };
      this._initialPaintDone = false;
      this._lastPaintedTotal = null;
      this._fillTransitionPrimed = new WeakSet();
      this._cache(); // cache nodes by roles
      this._initTrackLayout();
      this._initScrollBehavior();

      BWW.ready(state => {
        const cart = state.state?.cartState || null;
        if (cart) this.safeUpdate(cart);
        else if (!this._initialPaintDone) this.paint(this.lastSeen.value, { initial: true });
        state.watch("cartState", c => c && this.safeUpdate(c), { immediate: false });
      });
    }

    _getCartTotal(cart) {
      if (!cart) return 0;
      if (this.cartPriceType === "original_total_price") {
        return typeof cart.original_total_price === "number" ? cart.original_total_price : 0;
      }
      return typeof cart.total_price === "number" ? cart.total_price : 0;
    }

    _cache() {
      const q = name => this.root.querySelector(`[data-gwp="${name}"]`);
      const qa = name => this.root.querySelectorAll(`[data-gwp="${name}"]`);
      this.nodes = {
        indicators: qa("indicator"),
        markers: qa("marker"),
        segments: qa("segment"),
        fills: qa("fill"),
        next: q("nextText"),
        remainingAmount: q("remainingAmount"),
        nextAchievement: q("nextAchievement"),
        successMessage: q("successMessage"),
        labelsRow: q("labelsRow"),
      };
      // Store initial milestone data from Liquid
      this.nextThreshold = parseInt(this.root.dataset.nextThreshold || "0", 10) || 0;
      this.nextLabel = this.root.dataset.nextLabel || "";
      if (!this.nextLabel && this.nextThreshold > 0) {
        const ind = Array.from(this.nodes.indicators).find(
          x => parseInt(x.dataset.threshold || "0", 10) === this.nextThreshold
        );
        if (ind?.dataset.label) this.nextLabel = ind.dataset.label;
      }
    }

    _parseThresholds(s) {
      return s
        .split(",")
        .map(v => parseInt(v.trim(), 10))
        .filter(v => !isNaN(v))
        .sort((a, b) => a - b);
    }

    /**
     * Place each track segment in the horizontal gap between consecutive
     * marker tiles so progress is never hidden under a marker box.
     * Falls back to Liquid flex layout until the container is visible and measurable.
     */
    _initTrackLayout() {
      this._trackContainer = this.root.querySelector(".gwp-progressbar-container");
      this._trackFill = this._trackContainer?.querySelector(".gwp-progressbar-fill") || null;
      if (!this._trackContainer || !this._trackFill) return;

      this._syncTrackLayout = () => {
        const markers = Array.from(this.nodes.markers);
        const segments = Array.from(this.nodes.segments);
        if (!markers.length || !segments.length || !this._trackContainer || !this._trackFill) return;

        const containerRect = this._trackContainer.getBoundingClientRect();
        if (containerRect.width < 1) {
          this._trackFill.classList.remove("gwp-track--positioned");
          return;
        }

        let hasValidGap = false;

        segments.forEach((seg, index) => {
          const startMarker = markers[index];
          const endMarker = markers[index + 1];
          if (!startMarker || !endMarker) return;

          const startRect = startMarker.getBoundingClientRect();
          const endRect = endMarker.getBoundingClientRect();
          const left = startRect.right - containerRect.left;
          const width = endRect.left - startRect.right;

          if (width > 2) hasValidGap = true;

          seg.style.left = `${Math.round(left)}px`;
          seg.style.width = `${Math.max(0, Math.round(width))}px`;
        });

        if (hasValidGap) {
          this._trackFill.classList.add("gwp-track--positioned");
          if (!this._initialPaintDone) {
            this._initialPaintDone = true;
            this.paint(this.lastSeen.value, { initial: true });
          }
        } else {
          this._trackFill.classList.remove("gwp-track--positioned");
        }
      };

      this._scheduleTrackLayout = () => {
        requestAnimationFrame(() => {
          this._syncTrackLayout();
          requestAnimationFrame(() => this._syncTrackLayout());
        });
      };

      this._scheduleTrackLayout();

      if (typeof ResizeObserver !== "undefined") {
        this._trackResizeObserver = new ResizeObserver(() => this._scheduleTrackLayout());
        this._trackResizeObserver.observe(this._trackContainer);
        const markersRow = this.root.querySelector(".gwp-markers-row");
        if (markersRow) this._trackResizeObserver.observe(markersRow);
        Array.from(this.nodes.markers).forEach(marker => this._trackResizeObserver.observe(marker));
      }

      const cartDrawer = this.root.closest("cart-drawer-component");
      if (cartDrawer) {
        cartDrawer.addEventListener("dialog:open", this._scheduleTrackLayout);
      }

      document.addEventListener("section:morph", this._scheduleTrackLayout);
    }

    safeUpdate(cart) {
      const now = Date.now();
      const v = this._getCartTotal(cart);
      if (v === 0 && this.lastSeen.value > 0 && now - this.lastSeen.t < 400) {
        log("progress: transient zero ignored");
        return;
      }
      this.lastSeen = { value: v, t: now };
      this.paint(v);
    }

    paint(totalCents, options = {}) {
      const { initial = false } = options;
      const skipFill = this._lastPaintedTotal === totalCents;

      // markers
      this.nodes.indicators.forEach(ind => {
        const t = parseInt(ind.dataset.threshold || "0", 10) || 0;
        const marker = ind.querySelector('[data-gwp="marker"]');
        if (marker) marker.classList.toggle("GWP__marker--checked", totalCents >= t);
      });

      // fills
      if (!skipFill) {
        const segments = Array.from(this.nodes.segments);
        segments.forEach(seg => {
          const fill = seg.querySelector('[data-gwp="fill"]');
          if (!fill) return;
          const start = parseInt(seg.dataset.thresholdStart || "0", 10) || 0;
          const end = parseInt(seg.dataset.thresholdEnd || "0", 10) || 0;
          let w = 0;
          if (totalCents >= end) w = 100;
          else if (totalCents > start && end > start) {
            w = Math.min(100, Math.round(((totalCents - start) * 1000) / (end - start)) / 10);
          }
          const isFirstSet = initial || !this._fillTransitionPrimed.has(fill);
          if (isFirstSet) fill.style.transition = "none";
          fill.style.width = w + "%";
          if (isFirstSet) {
            this._fillTransitionPrimed.add(fill);
            requestAnimationFrame(() => {
              fill.style.transition = "";
            });
          }
        });
        this._lastPaintedTotal = totalCents;
      }

      if (this._scheduleTrackLayout) this._scheduleTrackLayout();

      // Update milestones
      this._updateMilestones(totalCents);

      // Max-threshold success state
      if (this.maxThreshold > 0 && totalCents >= this.maxThreshold) {
        // All tiers reached — show success, hide '$X away'
        if (this.nodes.successMessage) {
          this.nodes.successMessage.classList.remove("hidden");
        }
        if (this.nodes.nextAchievement) {
          this.nodes.nextAchievement.style.display = "none";
        }
        this._updateGwpState(null, true);
      } else {
        // Not all tiers reached — hide success, show '$X away' if applicable
        if (this.nodes.successMessage) {
          this.nodes.successMessage.classList.add("hidden");
        }

        // Update remaining amount for next milestone
        if (this.nextThreshold > 0 && this.nodes.remainingAmount) {
          const remainCents = this.nextThreshold - totalCents;
          const remainDollars = remainCents / 100;
          const remainFormatted = remainDollars % 1 === 0 ? remainDollars.toString() : remainDollars.toFixed(2);
          this.nodes.remainingAmount.textContent = `${this.currency}${remainFormatted}`;

          if (this.nodes.nextAchievement) {
            this.nodes.nextAchievement.style.display = "";
          }

          this._updateGwpState(remainFormatted, false);
        } else {
          this._updateGwpState(null, false);
        }
      }
    }

    _updateGwpState(remainFormatted, allReached) {
      BWW.ready(state => {
        let gwpMessage = null;
        if (allReached) {
          gwpMessage = "You've unlocked all gifts!";
        } else if (remainFormatted && this.nextLabel) {
          gwpMessage = `You're ${this.currency}${remainFormatted} away from ${this.nextLabel} !!`;
        }
        state.setState("gwpNextMessage", gwpMessage);
      });
    }

    _updateMilestones(totalCents) {
      // Recalculate next milestone (lowest threshold not yet reached)
      const nextT = this.thresholds.find(t => t > totalCents);
      let newNextThreshold = 0;
      let newNextLabel = "";
      if (nextT) {
        const ind = Array.from(this.nodes.indicators).find(x => parseInt(x.dataset.threshold || "0", 10) === nextT);
        newNextThreshold = nextT;
        newNextLabel = (ind && ind.dataset.label) || "";
      }

      if (newNextThreshold !== this.nextThreshold || newNextLabel !== this.nextLabel) {
        this.nextThreshold = newNextThreshold;
        this.nextLabel = newNextLabel;
        if (newNextLabel) {
          // Update the full message if milestone changed
          if (this.nodes.next) {
            const remainCents = newNextThreshold - totalCents;
            const remainDollars = remainCents / 100;
            const remainFormatted = remainDollars % 1 === 0 ? remainDollars.toString() : remainDollars.toFixed(2);
            this.nodes.next.innerHTML = `You're <span data-gwp="remainingAmount">${this.currency}${remainFormatted}</span> away from ${newNextLabel} !!`;
            // Re-cache the remainingAmount node
            this.nodes.remainingAmount = this.root.querySelector('[data-gwp="remainingAmount"]');
            this._updateGwpState(remainFormatted, false);
          }
          if (this.nodes.nextAchievement && this.nodes.nextAchievement.style.display === "none") {
            this.nodes.nextAchievement.style.display = "";
          }
        } else {
          // No next tier = all tiers reached
          if (this.nodes.nextAchievement) {
            this.nodes.nextAchievement.style.display = "none";
          }
          if (this.nodes.successMessage) {
            this.nodes.successMessage.classList.remove("hidden");
          }
          this._updateGwpState(null, true);
        }
      }
    }

    // ---------- Scroll behavior for label collapse/expand ----------
    _initScrollBehavior() {
      if (!this.nodes.labelsRow) return;

      this._labelsCollapsed = false;
      this._lastScrollY = 0;
      this._scrollHandler = null;
      this._scrollContainer = null;
      this._onDialogClose = () => {
        requestAnimationFrame(() => {
          this._lastScrollY = window.scrollY || window.pageYOffset;
        });
      };

      const isHeaderBar =
        this.root.classList.contains("header-gwp-progressbar") || !!this.root.closest(".header-gwp-progressbar");
      const cartDrawerWrapper = this.root.closest(".cart-drawer-gwp-progressbar");

      if (isHeaderBar) {
        this._scrollContainer = window;
        this._lastScrollY = window.scrollY || window.pageYOffset;
        this._scrollHandler = this._handleHeaderScroll.bind(this);
        window.addEventListener("scroll", this._scrollHandler, { passive: true });
        document.addEventListener("dialog:close", this._onDialogClose, true);
      } else if (cartDrawerWrapper) {
        // Progress bar sits above scroll-hint; scroll cart line items in sibling container
        const scrollParent =
          cartDrawerWrapper.parentElement?.querySelector(".cart-drawer__items") ||
          cartDrawerWrapper.closest("scroll-hint");
        if (scrollParent) {
          this._scrollContainer = scrollParent;
          this._lastScrollY = scrollParent.scrollTop;
          this._scrollHandler = this._handleCartDrawerScroll.bind(this);
          scrollParent.addEventListener("scroll", this._scrollHandler, { passive: true });
        }
      }
    }

    _isBodyScrollLocked() {
      return document.body.style.position === "fixed";
    }

    _setLabelsCollapsed(collapsed) {
      if (!this.nodes.labelsRow || collapsed === this._labelsCollapsed) return;
      this._labelsCollapsed = collapsed;
      this.nodes.labelsRow.classList.toggle("gwp-labels-row--collapsed", collapsed);
    }

    /** Collapse on scroll down; expand on scroll up. Ignore body scroll-lock from modals. */
    _handleScrollDirection(currentY) {
      const diff = currentY - this._lastScrollY;
      if (diff === 0) return;

      if (currentY <= 0) {
        this._setLabelsCollapsed(false);
      } else if (diff > 0) {
        this._setLabelsCollapsed(true);
      } else {
        this._setLabelsCollapsed(false);
      }

      this._lastScrollY = currentY;
    }

    _handleHeaderScroll() {
      if (this._isBodyScrollLocked()) return;
      this._handleScrollDirection(window.scrollY || window.pageYOffset);
    }

    _handleCartDrawerScroll() {
      if (!this._scrollContainer) return;
      this._handleScrollDirection(this._scrollContainer.scrollTop);
    }

    _destroyScrollBehavior() {
      if (this._scrollHandler) {
        if (this._scrollContainer === window) {
          window.removeEventListener("scroll", this._scrollHandler);
          document.removeEventListener("dialog:close", this._onDialogClose, true);
        } else if (this._scrollContainer) {
          this._scrollContainer.removeEventListener("scroll", this._scrollHandler);
        }
        this._scrollHandler = null;
        this._scrollContainer = null;
      }
    }

    _destroyTrackLayout() {
      if (this._trackResizeObserver) {
        this._trackResizeObserver.disconnect();
        this._trackResizeObserver = null;
      }
      if (this._scheduleTrackLayout) {
        document.removeEventListener("section:morph", this._scheduleTrackLayout);
        const cartDrawer = this.root.closest("cart-drawer-component");
        if (cartDrawer) {
          cartDrawer.removeEventListener("dialog:open", this._scheduleTrackLayout);
        }
      }
    }

    destroy() {
      this._destroyScrollBehavior();
      this._destroyTrackLayout();
    }

    static initAll() {
      document.querySelectorAll('[data-component="gwp-progressbar"]:not([data-gwp-initialized])').forEach(el => {
        el.dataset.gwpInitialized = "true";
        if (!el._gwpProgress) el._gwpProgress = new GwpProgress(el);
      });
    }
  }

  // ---------- GWP Tier Manager (role-agnostic) ----------
  class GwpTierManager {
    // Class-level lock to prevent multiple instances from conflicting
    static _globalLocks = new Map();

    constructor(root) {
      this.root = root;
      this.variantId = GwpTierManager._toInt(root.dataset.gwpVariantId);
      this.tier = GwpTierManager._toInt(root.dataset.gwpTier);
      this.target = GwpTierManager._toInt(root.dataset.gwpTargetThreshold); // cents
      this.cartPriceType = root.dataset.cartPriceType || "total_price";
      this._unwatch = null;
      this._lastState = { meets: null, has: null, qty: null };
      this._lockKey = `tier-${this.tier}-variant-${this.variantId}`;

      if (!this.variantId || !this.tier || !this.target) {
        log("tier: missing attrs, skip", { v: this.variantId, t: this.tier, thr: this.target });
        return;
      }

      // Prevent multiple instances for same tier+variant
      if (GwpTierManager._globalLocks.has(this._lockKey)) {
        console.warn(`⚠️ Duplicate GwpTierManager for ${this._lockKey}, skipping`);
        return;
      }

      GwpTierManager._globalLocks.set(this._lockKey, false);
      this._attach();
    }

    static _toInt(v) {
      const n = parseInt(v, 10);
      return Number.isFinite(n) ? n : 0;
    }

    _isLocked() {
      return GwpTierManager._globalLocks.get(this._lockKey) === true;
    }

    _setLock(locked) {
      GwpTierManager._globalLocks.set(this._lockKey, locked);
    }

    _attach() {
      BWW.ready(state => {
        const cart = state.state?.cartState || BWW.getCart() || null;
        if (cart) this._evaluate(cart);

        this._unwatch = state.watch(
          "cartState",
          c => {
            if (c) this._evaluate(c);
          },
          { immediate: false }
        );

        // Gentle post-init poll for edge cases
        let n = 0,
          max = 4;
        const timer = setInterval(() => {
          const c = BWW.getCart();
          if (c) this._evaluate(c);
          if (++n >= max) clearInterval(timer);
        }, 1000);
      });
    }

    _propIsGwp(props) {
      if (!props) return false;
      if (props._isGWP !== undefined) return ["true", true, 1, "1"].includes(props._isGWP);
      if (Array.isArray(props)) {
        const p = props.find(x => (x?.name || x?.first) === "_isGWP");
        const v = p && (p.value ?? p.last);
        return ["true", true, 1, "1"].includes(v);
      }
      return false;
    }

    _getCartTotal(cart) {
      if (!cart) return 0;
      if (this.cartPriceType === "original_total_price") {
        return typeof cart.original_total_price === "number" ? cart.original_total_price : 0;
      }
      return typeof cart.total_price === "number" ? cart.total_price : 0;
    }

    _findGwp(cart) {
      if (!cart?.items?.length || !this.variantId) return null;
      const idx = cart.items.findIndex(it => it.variant_id === this.variantId && this._propIsGwp(it.properties));
      return idx === -1 ? null : { item: cart.items[idx], line: idx + 1 };
    }

    async _evaluate(cart) {
      // Ignore transient zero carts (Shopify intermediate states)
      if (!cart?.items?.length && this._getCartTotal(cart) === 0) {
        const lastTotal = this._lastState.total || 0;
        if (lastTotal > 0) {
          return;
        }
      }

      const total = this._getCartTotal(cart);
      const meets = total >= this.target;
      const gwp = this._findGwp(cart);
      const has = !!gwp;
      const qty = gwp?.item?.quantity || 0;

      // If locked, skip silently
      if (this._isLocked()) {
        return;
      }

      // Prevent infinite loops - skip if state hasn't changed
      if (
        this._lastState.meets === meets &&
        this._lastState.has === has &&
        this._lastState.qty === qty &&
        this._lastState.total === total
      ) {
        return;
      }

      // Update memo before proceeding
      this._lastState = { meets, has, qty, total };

      log("tier: eval", { total, target: this.target, meets, has, line: gwp?.line, qty });

      if (meets && !has) {
        return this._add();
      }
      if (!meets && has) {
        return this._remove(gwp);
      }
      if (meets && has && qty !== 1) {
        return this._qty(gwp, 1);
      }
    }

    async _add() {
      if (this._isLocked()) return;
      this._setLock(true);

      try {
        const utils = window.GwpCartUtils;
        if (utils) {
          const { data, sections } = await utils.addGwp(this.variantId, String(this.tier));
          utils.dispatchCartUpdate(data, sections);
        } else {
          const shopify = await BWW.shopify();
          await shopify.addToCart(this.variantId, 1, { _isGWP: "true", _gwp_tier: String(this.tier) });
        }
      } catch (e) {
        err("tier: add failed", e);
      } finally {
        this._setLock(false);
      }
    }

    async _remove(gwp) {
      if (this._isLocked()) return;
      this._setLock(true);

      try {
        const utils = window.GwpCartUtils;
        if (utils) {
          const { data, sections } = await utils.removeGwp(gwp.line);
          utils.dispatchCartUpdate(data, sections);
        } else {
          const shopify = await BWW.shopify();
          await shopify.changeCartItem(gwp.line, 0);
        }
      } catch (e) {
        err("tier: remove failed", e);
      } finally {
        this._setLock(false);
      }
    }

    async _qty(gwp, q) {
      if (this._isLocked()) return;
      this._setLock(true);

      try {
        const utils = window.GwpCartUtils;
        if (utils) {
          const { data, sections } = await utils.updateGwpQty(gwp.line, q);
          utils.dispatchCartUpdate(data, sections);
        } else {
          const shopify = await BWW.shopify();
          await shopify.changeCartItem(gwp.line, q);
        }
      } catch (e) {
        err("tier: qty failed", e);
      } finally {
        this._setLock(false);
      }
    }

    static initAll() {
      // Clear any existing locks
      GwpTierManager._globalLocks.clear();

      document.querySelectorAll('[data-component="gwp-progressbar"]:not([data-gwp-tier-initialized])').forEach(el => {
        if (!el.dataset.gwpVariantId || !el.dataset.gwpTier || !el.dataset.gwpTargetThreshold) return;
        el.dataset.gwpTierInitialized = "true";
        if (!el._gwpTier) el._gwpTier = new GwpTierManager(el);
      });
    }
  }

  // ---------- Body class sync (AJAX cart / header morph only — padding is CSS) ----------
  let gwpBodyPaddingRaf = 0;

  function getCartItemCount() {
    if (window.BWW?.getCartCount) {
      const count = window.BWW.getCartCount();
      if (typeof count === "number") return count;
    }
    const cart = window.bwwState?.state?.cartState;
    if (cart && typeof cart.item_count === "number") return cart.item_count;
    const bodyCount = document.body?.dataset?.cartItemCount;
    if (bodyCount !== undefined && bodyCount !== "") {
      const parsed = parseInt(bodyCount, 10);
      if (!isNaN(parsed)) return parsed;
    }
    return null;
  }

  /**
   * Toggle has-gwp-global-bar after AJAX cart changes (header morph adds/removes the bar).
   * Initial load is handled by SSR + components.css — do not call on boot.
   */
  function syncGwpGlobalBarBodyPadding() {
    const body = document.body;
    if (!body) return;

    const cartCount = getCartItemCount();
    if (cartCount === null) return;

    const hasBar = !!document.querySelector("header-component .header-gwp-progressbar");
    const showBar = hasBar && cartCount > 0;

    body.classList.toggle("has-gwp-global-bar", showBar);
    body.dataset.cartItemCount = String(cartCount);
  }

  function scheduleSyncGwpGlobalBarBodyPadding() {
    cancelAnimationFrame(gwpBodyPaddingRaf);
    gwpBodyPaddingRaf = requestAnimationFrame(() => {
      gwpBodyPaddingRaf = requestAnimationFrame(syncGwpGlobalBarBodyPadding);
    });
  }

  /**
   * @param {Element} node
   * @returns {HTMLElement[]}
   */
  function findGwpProgressbarRoots(node) {
    if (!(node instanceof HTMLElement)) return [];
    if (node.matches?.('[data-component="gwp-progressbar"]')) return [node];
    return Array.from(node.querySelectorAll('[data-component="gwp-progressbar"]'));
  }

  /** @param {HTMLElement} root */
  function teardownGwpProgressbar(root) {
    if (root._gwpProgress?.destroy) {
      root._gwpProgress.destroy();
    }
    delete root._gwpProgress;
    delete root._gwpTier;
    delete root.dataset.gwpInitialized;
    delete root.dataset.gwpTierInitialized;
  }

  // ---------- Boot ----------
  function boot() {
    GwpProgress.initAll();
    GwpTierManager.initAll();
  }

  /**
   * Initialize with delay for dynamically added elements
   * Ensures DOM is fully ready after morphSection
   */
  function bootWithDelay() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(boot, 50);
      });
    });
  }

  /**
   * Observe for dynamically added GWP progressbar elements
   * Handles cart going from empty to having items
   */
  function observeGwpElements() {
    const observer = new MutationObserver(mutations => {
      let barRemoved = false;

      for (const mutation of mutations) {
        if (mutation.type !== "childList") continue;

        for (const node of mutation.removedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          for (const root of findGwpProgressbarRoots(node)) {
            teardownGwpProgressbar(root);
          }
          if (
            (node.matches && node.matches(".header-gwp-progressbar")) ||
            (node.querySelector && node.querySelector(".header-gwp-progressbar"))
          ) {
            barRemoved = true;
          }
        }

        if (mutation.addedNodes.length > 0) {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if the node itself is a GWP progressbar
              if (node.matches && node.matches('[data-component="gwp-progressbar"]:not([data-gwp-initialized])')) {
                initSingleElement(node);
              }
              // Check if the node contains GWP progressbar elements
              if (node.querySelectorAll) {
                const elements = node.querySelectorAll(
                  '[data-component="gwp-progressbar"]:not([data-gwp-initialized])'
                );
                elements.forEach(el => initSingleElement(el));
              }
            }
          }
        }
      }

      if (barRemoved) {
        scheduleSyncGwpGlobalBarBodyPadding();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Initialize a single GWP progressbar element
   * @param {HTMLElement} el - The element to initialize
   */
  function initSingleElement(el) {
    // Mark as initialized to prevent duplicates
    el.dataset.gwpInitialized = "true";

    // Initialize with delay to ensure DOM is ready
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (!el._gwpProgress) {
            el._gwpProgress = new GwpProgress(el);
          }
          if (el.dataset.gwpVariantId && el.dataset.gwpTier && el.dataset.gwpTargetThreshold) {
            if (!el._gwpTier) {
              el.dataset.gwpTierInitialized = "true";
              el._gwpTier = new GwpTierManager(el);
            }
          }
        }, 50);
      });
    });
  }

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      boot();
      observeGwpElements();
    });
  } else {
    boot();
    observeGwpElements();
  }

  // Handle Shopify section load events
  document.addEventListener("shopify:section:load", boot);

  function onCartUpdate(event) {
    const itemCount = event.detail?.data?.itemCount ?? event.detail?.resource?.item_count ?? getCartItemCount();

    if (typeof itemCount === "number") {
      scheduleSyncGwpGlobalBarBodyPadding();
    }

    bootWithDelay();
  }

  document.addEventListener("cart:update", onCartUpdate);

  function onSectionMorph() {
    scheduleSyncGwpGlobalBarBodyPadding();
    // Header GWP is injected via morph after cart:update — cart:update boot often runs too early
    bootWithDelay();
  }

  document.addEventListener("section:morph", onSectionMorph);

  // Optional: expose for debug
  window.__GWP__ = { boot, bootWithDelay, syncGwpGlobalBarBodyPadding, GwpProgress, GwpTierManager };
})();
